import {
  chakra,
  Input,
  InputGroup,
  InputLeftElement,
  InputProps,
  shouldForwardProp,
  useColorModeValue,
  useToken,
} from "@chakra-ui/react";
import { useTextWidth } from "@tag0/use-text-width";
import { isValidMotionProp, motion } from "framer-motion";
import { FocusEventHandler, useCallback, useRef, useState } from "react";

import { IcoSearch } from "@/common/components/Icons";

type Props = InputProps & {
  expandable?: boolean;
};

const MotionInput = chakra(motion.input, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop),
});

export default function SearchInput({
  value,
  placeholder = "Search by name/ID",
  onFocus,
  onBlur,
  expandable = true,
  ...inputProps
}: Props) {
  const searchIconColor = useColorModeValue("gray.400", "gray.600");
  const inputRef = useRef<HTMLInputElement>(null);

  const [isFocused, setIsFocused] = useState(false);

  const handleFocus: FocusEventHandler<HTMLInputElement> = useCallback(
    (...args) => {
      setIsFocused(true);
      onFocus?.(...args);
    },
    [onFocus],
  );

  const handleBlur: FocusEventHandler<HTMLInputElement> = useCallback(
    (...args) => {
      setIsFocused(false);
      onBlur?.(...args);
    },
    [onBlur],
  );

  const fontSize = useToken("fontSizes", "sm");
  const fontFamily = useToken("fonts", "body");
  const expandedWidth =
    useTextWidth({
      text: placeholder,
      font: `${fontSize} ${fontFamily}`,
    }) + 54;

  return (
    <InputGroup width="auto">
      <InputLeftElement
        color={!value ? searchIconColor : undefined}
        cursor="text"
        onClick={() => inputRef.current?.focus()}
      >
        <IcoSearch size="md" />
      </InputLeftElement>
      <Input
        ref={inputRef}
        as={MotionInput}
        placeholder={!isFocused && expandable ? "Search" : placeholder}
        size="sm"
        type="search"
        value={value}
        whileFocus={{
          width: expandable ? `${expandedWidth}px` : undefined,
          transition: {
            type: "spring",
            duration: 0.15,
            bounce: 0.3,
          },
        }}
        width={value && expandable ? `${expandedWidth}px` : "96px"}
        onBlur={handleBlur}
        onFocus={handleFocus}
        {...inputProps}
      />
    </InputGroup>
  );
}
